import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { appUrl } from "../../../../../../utils/config";
import { useApiBaseUrl } from "../../CommonMethods/CommonMethods";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  Checkbox,
  Select,
  MenuItem,
  IconButton,
  Button,
  Dialog,
  Typography,
  InputBase,
  CircularProgress,
  makeStyles,
  withStyles,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import GetAppIcon from "@material-ui/icons/GetApp";
import ConfigureIDocTriggerPointDialog from "../../SponsorNetworkTabs/ConfigureIDocTriggerPointDialog";
import ConfigureIDocIngestionDialog from "./ConfigureIDocIngestionDialog";
import {
  MODULE_OPTIONS,
  DEFAULT_PRODUCT_TYPES,
  DEFAULT_IDOC_FIELDS,
  isShipmentModule,
  genId,
} from "./erpToErpConfigData";

const useStyles = makeStyles((theme) => ({
  sectionBlock: {
    marginBottom: theme.spacing(5),
  },
  subHeading: {
    fontFamily: "Roboto, Helvetica, Arial, sans-serif",
    fontWeight: 800,
    fontSize: 18,
    marginBottom: theme.spacing(1),
  },
  // Replicates .stepper-container form h6 / .update-cmo-details h6 (CMOConfigDetails.css)
  // directly in JS rather than relying on that global selector chain — this component
  // renders in four different DOM contexts (Add stepper, Update, View, Review) and isn't
  // always inside a <form>, so the global rule doesn't reliably reach it otherwise.
  mainHeading: {
    fontSize: "24px !important",
    lineHeight: "39px !important",
    fontWeight: "600 !important",
    margin: "55px 0 70px 0 !important",
    position: "relative",
    "&::after": {
      content: '""',
      display: "block",
      width: 55,
      height: 5,
      backgroundColor: "#707070",
      position: "absolute",
      left: 0,
      top: 60,
    },
  },
  iosSwitch: {
    "& > *": {
      height: "22px !important",
    },
    "& .MuiSwitch-switchBase .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "& .MuiSwitch-switchBase": {
      left: "2px",
      top: "-1px",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      left: "auto",
      right: "18px",
    },
    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-track": {
      backgroundColor: "#1890ff !important",
    },
  },
  checkedSwitch: {
    position: "absolute",
    width: "16px !important",
    left: "12px",
    top: "-3px",
    color: "white",
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    "& td, & th": {
      borderRight: "1px solid #e0e0e0",
      padding: "8px 16px !important",
    },
    "& td:last-child, & th:last-child": {
      borderRight: "none",
    },
  },
  tableHead: {
    backgroundColor: "#f9f9fc",
  },
  tableCell: {
    fontSize: 15,
  },
  na: {
    color: "#888",
    fontStyle: "italic",
  },
  // Overrides MUI's default faded/gray disabled-Select look for readOnly mode — the
  // dropdown stays non-interactive (disabled), but its text renders like normal content.
  selectDisabledText: {
    color: "#000 !important",
    opacity: "1 !important",
    "-webkit-text-fill-color": "#000",
  },
  iconBtn: {
    padding: 4,
  },
  editIconOrange: {
    color: "#ec6535",
  },
  editIconGray: {
    color: "#bbb",
  },
  link: {
    color: "#1890FF",
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: 13,
  },
  addFieldButton: {
    marginTop: theme.spacing(1),
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 20,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  subTabsContainer: {
    display: "flex",
    gap: theme.spacing(3),
    borderBottom: "1px solid #ddd",
    marginBottom: theme.spacing(2),
  },
  subTab: {
    padding: "8px 4px",
    cursor: "pointer",
    fontSize: 14,
    color: "#555",
    position: "relative",
  },
  activeSubTab: {
    color: "#1890FF",
    fontWeight: 600,
  },
  subTabIndicator: {
    position: "absolute",
    bottom: -1,
    left: 0,
    right: 0,
    height: 2,
    backgroundColor: "#1890FF",
  },
  noModulesNote: {
    color: "#888",
    fontSize: 14,
    padding: theme.spacing(2, 0),
  },
  varianceField: {
    marginTop: theme.spacing(2),
    maxWidth: 320,
  },
  modalRoot: {
    fontFamily: theme.typography.fontFamily,
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #eee",
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: "#000",
  },
  modalCancelX: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: "#888",
    cursor: "pointer",
    fontSize: 13,
  },
  modalBody: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
  },
  modalInfoBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(4),
    backgroundColor: "#eef3fb",
    borderRadius: 6,
    padding: theme.spacing(2, 3),
  },
  infoLabel: {
    fontSize: 11,
    color: "#888",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 600,
    color: "#000",
  },
  requiredStar: {
    color: "#e53935",
  },
  viewTable: {
    border: "1px solid #ddd",
    minWidth: 700,
  },
  viewHeaderCell: {
    backgroundColor: "#ec6535",
    color: "#fff",
    fontWeight: 600,
    fontSize: 12,
    padding: "8px 10px",
    whiteSpace: "nowrap",
  },
  viewBodyCell: {
    fontSize: 12,
    padding: "8px 10px",
    verticalAlign: "top",
  },
  viewTableWrapper: {
    maxHeight: 420,
    overflow: "auto",
  },
  searchBox: {
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 13,
    width: 220,
  },
  downloadBtn: {
    backgroundColor: "#1890FF",
    color: "#fff",
    borderRadius: 4,
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    "&:hover": {
      backgroundColor: "#146fce",
    },
  },
  errorText: {
    color: "#c62828",
    fontSize: 13,
  },
  // Parameters ("Click to view") modal — dark navy title/subtitle bars + striped table,
  // distinct from the orange-header viewTable/viewHeaderCell styling other popups use.
  paramsTopBar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 3),
  },
  paramsTableWrapper: {
    maxHeight: 420,
    overflow: "auto",
    "& tbody tr:nth-child(odd)": {
      backgroundColor: "#eaf1f8",
    },
    "& tbody tr:nth-child(even)": {
      backgroundColor: "#fff",
    },
  },
  paramsTable: {
    minWidth: 700,
  },
  paramsHeaderCell: {
    backgroundColor: "#1f3a5f",
    color: "#fff",
    fontWeight: 700,
    fontSize: 12,
    padding: "10px 12px",
    whiteSpace: "nowrap",
  },
  paramsBodyCell: {
    fontSize: 12,
    padding: "8px 12px",
    verticalAlign: "top",
  },
  paramsCloseIcon: {
    display: "flex",
    alignItems: "center",
    color: "#555",
    cursor: "pointer",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #eee",
  },
  modalCancelBtn: {
    border: "1px solid #ccc",
    color: "#000",
    borderRadius: 4,
    padding: "6px 20px",
    textTransform: "none",
  },
  modalSaveBtn: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: 4,
    padding: "6px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
  outlinedAddBtn: {
    border: "1px solid #ec6535",
    color: "#ec6535",
    borderRadius: 20,
    padding: "6px 18px",
    textTransform: "none",
    fontWeight: 600,
    marginTop: theme.spacing(1),
  },
  compactSelect: {
    minWidth: 130,
    fontSize: 12,
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
  },
  timeInput: {
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "6px 10px",
    fontSize: 13,
    width: 160,
  },
  deleteIconRed: {
    color: "#e53935",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
    },
  },
}));

// Same iOS-style toggle used by sibling sections (e.g. ProductsTypesSection.jsx) — kept
// visually consistent across the CMO config UI rather than the plain default MUI Switch.
const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 44,
    height: 22,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "& .MuiSwitch-track": {
      backgroundColor: "#c6c6c6 !important",
    },
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#1890ff !important",
        opacity: 1,
        border: "none",
      },
      "& .MuiSwitch-track": {
        backgroundColor: "#1890ff !important",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 18,
    height: 18,
  },
  track: {
    borderRadius: 26 / 2,
    backgroundColor: "#c6c6c6",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => (
  <Switch
    focusVisibleClassName={classes.focusVisible}
    disableRipple
    disabled={props.disabled}
    classes={{
      root: classes.root,
      switchBase: classes.switchBase,
      thumb: classes.thumb,
      track: classes.track,
      checked: classes.checked,
    }}
    {...props}
  />
));

const buildInitialProductTypeConfigs = (productTypes) =>
  productTypes.map((pt) => ({
    productTypeID: pt.productTypeID,
    productTypeName: pt.productTypeName,
    erpConnectivity: pt.erpConnectivity || "No",
    modules: pt.modules || [],
  }));

const ErpToErpConfigurationSection = forwardRef(
  (
    {
      productTypes = DEFAULT_PRODUCT_TYPES,
      editMode = true,
      initialData = {},
      onChange,
      cmoName = "",
      vendorMosymphonyID,
      brandOwnerMosymphonyID,
      headerActions = null,
    },
    ref
  ) => {
    const classes = useStyles();
    const readOnly = !editMode;
    const API_BASE_URL = useApiBaseUrl(appUrl);

    // Modules actually configured as seed data on the backend (fetchAllModules) — only
    // these appear at all in the Modules dropdown below (everything else in MODULE_OPTIONS
    // is hidden, not just grayed out), and their collabrixModuleID drives the
    // fetchAllMessages/{id} call below for Module Messages for IDocs. null = not loaded yet.
    const [apiModules, setApiModules] = useState(null); // [{ collabrixModuleID, moduleName }]

    useEffect(() => {
      if (!API_BASE_URL) return;
      axios
        .get(`${API_BASE_URL}/fetchAllModules`)
        .then((response) => {
          setApiModules(response?.data?.modulesList || []);
        })
        .catch((err) => {
          console.error("Error fetching modules:", err);
          setApiModules([]);
        });
    }, [API_BASE_URL]);

    // Data Elements seed data — drives both the IDoc Configurations "Field" dropdown
    // (docx: "The drop down list for Field column will come from Data Elements seed
    // data list") and the Ingestion popup's "Data Elements" dropdown, which is the
    // same seed data concept.
    const [dataElements, setDataElements] = useState(null); // null = not loaded yet

    useEffect(() => {
      if (!API_BASE_URL) return;
      axios
        .get(`${API_BASE_URL}/fetchDataElements`)
        .then((response) => {
          const ok = response?.data?.status === 200;
          const list = ok ? response?.data?.data?.dataElementsList : null;
          setDataElements((list || []).filter((el) => !el.markForDelete));
        })
        .catch((err) => {
          console.error("Error fetching data elements:", err);
          setDataElements([]);
        });
    }, [API_BASE_URL]);

    // Data Element labels, deduplicated (the seed data can carry the same label under
    // more than one category/id — e.g. "Site" for both Inventory and PO Collaboration).
    const dataElementLabels = Array.from(
      new Set((dataElements || []).map((el) => el.dataElementLabel).filter(Boolean))
    );

    // Strips a trailing parenthetical (e.g. "Goods Receipt (current Batch Execution)"
    // -> "Goods Receipt") so MODULE_OPTIONS labels can be matched against the backend's
    // plainer moduleName values.
    const normalizeModuleName = (name = "") =>
      name.replace(/\s*\([^)]*\)/g, "").trim().toLowerCase();

    // Only modules the backend actually has seed data for are shown in the dropdown at
    // all. While the fetch is still in flight (apiModules === null), fall back to
    // MODULE_OPTIONS' own static disabled flags so the UI isn't empty for a beat.
    const visibleModuleOptions =
      apiModules === null
        ? MODULE_OPTIONS.filter((opt) => !opt.disabled)
        : MODULE_OPTIONS.filter((opt) =>
            apiModules.some((m) => normalizeModuleName(m.moduleName) === normalizeModuleName(opt.label))
          );

    const getModuleIdForKey = (moduleKey) => {
      const opt = MODULE_OPTIONS.find((m) => m.key === moduleKey);
      const match =
        opt && apiModules
          ? apiModules.find((m) => normalizeModuleName(m.moduleName) === normalizeModuleName(opt.label))
          : null;
      return match ? match.collabrixModuleID : null;
    };

    const [productTypeConfigs, setProductTypeConfigs] = useState(
      () =>
        initialData.productTypeConfigs ||
        buildInitialProductTypeConfigs(productTypes)
    );
    const [idocFields, setIdocFields] = useState(
      () => initialData.idocFields || DEFAULT_IDOC_FIELDS.map((f) => ({ ...f, id: genId("field") }))
    );
    const [moduleMessages, setModuleMessages] = useState(() => initialData.moduleMessages || {});
    // Live catalog of what each module's Sponsor-activated messages/Build-Versions are
    // (fetchAllMessages/{collabrixModuleID}) — the source of truth for what a CMO admin
    // can pick from below; moduleMessages (above) is the CMO's own editable selection.
    const [seedMessagesByModule, setSeedMessagesByModule] = useState({});
    const [seedMessagesLoading, setSeedMessagesLoading] = useState({});
    const fetchedModuleKeysRef = useRef(new Set());
    const [shipmentQtyVarianceLimit, setShipmentQtyVarianceLimit] = useState(
      initialData.shipmentQtyVarianceLimit || ""
    );

    // /viewCMODetails returns erpConfigurations nested inside vendorPlantDetails, shaped
    // as { productTypeList, idocConfigList } (confirmed via live response — NOT
    // vendorProductType/idocConfig as getPayload() below sends; see note there), not the
    // internal productTypeConfigs/idocFields shape above. Update/View pass that raw
    // object straight through as initialData; these derive productTypeConfigs and
    // idocFields from it once apiModules/dataElements have loaded (needed to reverse
    // moduleID -> module key and dataElementID -> field label), used both by the
    // hydration effect below and by resetForm() (Discard). moduleMessages isn't derived
    // here — its CMO-specific selection already comes from fetchAllVendorMessages'
    // vendorActivated/collabrixVendorMessageID fields (see fetchMessagesForModuleKey), and
    // the confirmed erpConfigurations shape has no vendorMessagesList on read anyway.
    const deriveProductTypeConfigsFromRaw = (raw, prev) => {
      if (!Array.isArray(raw?.productTypeList) || raw.productTypeList.length === 0) return prev;
      return prev.map((p) => {
        const match = raw.productTypeList.find((v) => v.productTypeID === p.productTypeID);
        if (!match) return p;
        const moduleKeys = String(match.moduleID || "")
          .split(",")
          .map((id) => id.trim())
          .filter(Boolean)
          .map((id) => {
            const apiModule = (apiModules || []).find((m) => String(m.collabrixModuleID) === id);
            if (!apiModule) return null;
            const opt = MODULE_OPTIONS.find(
              (o) => normalizeModuleName(o.label) === normalizeModuleName(apiModule.moduleName)
            );
            return opt?.key || null;
          })
          .filter(Boolean);
        return {
          ...p,
          erpConnectivity: match.erpConnectivity ? "Yes" : "No",
          modules: moduleKeys,
        };
      });
    };

    const defaultIdocFieldNames = DEFAULT_IDOC_FIELDS.map((d) => d.field);

    const deriveIdocFieldsFromRaw = (raw, fallback) => {
      if (!Array.isArray(raw?.idocConfigList) || raw.idocConfigList.length === 0) return fallback;
      return raw.idocConfigList.map((f) => {
        const field =
          (dataElements || []).find((el) => String(el.dataElementId) === String(f.dataElementID))
            ?.dataElementLabel || "";
        return {
          id: genId("field"),
          field,
          // The 3 default IDoc field mappings (PO Number, Sponsor Product Code, Sponsor
          // Material Code) are locked (not removable/editable) per spec — the initial
          // useState default marks these via DEFAULT_IDOC_FIELDS' own isDefault: true, but
          // that flag isn't part of the backend's idocConfigList, so it has to be
          // re-derived here by field name or these rows would come back fully editable
          // with a delete button after hydrating from real saved data.
          isDefault: defaultIdocFieldNames.includes(field),
          leadingZeros: f.removeLeadingZeros ? "Yes" : "No",
          maxCharLength: f.removeLeadingZeros ? f.maxCharLengthToBeExtracted ?? "" : "",
        };
      });
    };

    const rawHydratedRef = useRef(false);
    useEffect(() => {
      if (rawHydratedRef.current) return;
      if (apiModules === null || dataElements === null) return;
      rawHydratedRef.current = true;
      setProductTypeConfigs((prev) => deriveProductTypeConfigsFromRaw(initialData, prev));
      setIdocFields((prev) => deriveIdocFieldsFromRaw(initialData, prev));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiModules, dataElements]);

    // Keep product type rows in sync if the parent's product type list changes
    // (e.g. new product types activated) but preserve any existing configuration.
    // productTypeName is always refreshed from the incoming prop — it arrives blank
    // on first render (useProductTypeNames resolves it asynchronously) and must not
    // stay frozen once the parent re-renders with the real name.
    useEffect(() => {
      setProductTypeConfigs((prev) =>
        productTypes.map((pt) => {
          const existing = prev.find((p) => p.productTypeID === pt.productTypeID);
          return existing
            ? { ...existing, productTypeName: pt.productTypeName || existing.productTypeName }
            : {
                productTypeID: pt.productTypeID,
                productTypeName: pt.productTypeName,
                erpConnectivity: "No",
                modules: [],
              };
        })
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productTypes.map((p) => `${p.productTypeID}:${p.productTypeName}`).join(",")]);

    const [modulesEditState, setModulesEditState] = useState({});
    const [modulesEditPrev, setModulesEditPrev] = useState({});

    const [activeModuleTab, setActiveModuleTab] = useState(0);

    const [ingestionModal, setIngestionModal] = useState(null); // { moduleKey, messageId, rowId }
    const [triggerModal, setTriggerModal] = useState(null); // { moduleKey, messageId, rowId }
    const [viewModal, setViewModal] = useState(null); // { description, excelFieldMappingFileID, messageId, buildVersion }
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [fileLoadError, setFileLoadError] = useState(null);
    const [viewFileRows, setViewFileRows] = useState([]); // sheet_to_json(header:1) rows
    const [viewFileURL, setViewFileURL] = useState(null); // Blob URL for the Download button
    const [viewFileName, setViewFileName] = useState("");
    const [viewSearch, setViewSearch] = useState("");

    const notify = (nextState = {}) => {
      if (onChange) {
        onChange(
          {
            productTypeConfigs: nextState.productTypeConfigs || productTypeConfigs,
            idocFields: nextState.idocFields || idocFields,
            moduleMessages: nextState.moduleMessages || moduleMessages,
            shipmentQtyVarianceLimit:
              nextState.shipmentQtyVarianceLimit !== undefined
                ? nextState.shipmentQtyVarianceLimit
                : shipmentQtyVarianceLimit,
          },
          true
        );
      }
    };

    useImperativeHandle(ref, () => ({
      getFormData: () => ({
        productTypeConfigs,
        idocFields,
        moduleMessages,
        shipmentQtyVarianceLimit,
      }),
      // Real /updateVendorPlantDetails contract for this step. Note the write contract's
      // field names (vendorProductType/idocConfig) differ from what /viewCMODetails
      // returns on read (erpConfigurations.productTypeList/idocConfigList, see
      // deriveProductTypeConfigsFromRaw/deriveIdocFieldsFromRaw above) — the two aren't
      // symmetric, this is the confirmed write-side shape. collabrixVendorMessageID
      // round-trips the real ID fetched via fetchAllVendorMessages when this CMO already
      // has a vendor_messages record, falling back to 0 (create) only for a row it is
      // activating for the first time. A row with no record that is unselected or
      // removed is not sent at all — the backend would otherwise create a fresh record
      // for every 0-ID entry. A deletion whose record is still used by a visible row
      // (removed then re-added/swapped back) is dropped, so one record isn't both
      // updated and deleted in the same transaction.
      getPayload: () => ({
        updateERPConfigurationFlag: true,
        vendorMosymphonyID: vendorMosymphonyID ?? null,
        brandOwnerMosymphonyID: brandOwnerMosymphonyID ?? null,
        // erpConfigurations is a top-level sibling of vendorPlantDetails (not nested
        // inside it) for both the Add-flow stepper and Update-flow payloads.
        erpConfigurations: {
          vendorProductType: productTypeConfigs.map((p) => ({
            productTypeID: p.productTypeID,
            moduleID:
              p.erpConnectivity === "Yes"
                ? p.modules
                    .map((key) => getModuleIdForKey(key))
                    .filter((id) => id != null)
                    .join(",")
                : "",
            erpConnectivity: p.erpConnectivity === "Yes",
          })),
          idocConfig: idocFields.map((f) => ({
            dataElementID: (dataElements || []).find((el) => el.dataElementLabel === f.field)?.dataElementId ?? null,
            removeLeadingZeros: f.leadingZeros === "Yes",
            maxCharLengthToBeExtracted: f.leadingZeros === "Yes" ? f.maxCharLength : null,
          })),
          vendorMessagesList: Object.entries(moduleMessages).flatMap(([moduleKey, messages]) => {
            const collabrixModuleID = getModuleIdForKey(moduleKey);
            return messages.flatMap((m) => {
              const liveVendorMessageIDs = m.rows
                .filter((r) => !r.markForDelete && r.collabrixVendorMessageID)
                .map((r) => r.collabrixVendorMessageID);
              return m.rows
                .filter((r) =>
                  r.collabrixVendorMessageID
                    ? !(r.markForDelete && liveVendorMessageIDs.includes(r.collabrixVendorMessageID))
                    : !r.markForDelete && m.selected
                )
                .map((r) => ({
                  collabrixMessageID: r.collabrixMessageID ?? null,
                  collabrixModuleID,
                  collabrixVendorMessageID: r.collabrixVendorMessageID || 0,
                  activated: r.markForDelete ? false : m.selected,
                  markForDelete: !!r.markForDelete,
                  vendorMosymphonyID: vendorMosymphonyID ?? null,
                  brandOwnerMosymphonyID: brandOwnerMosymphonyID ?? null,
                }));
            });
          }),
        },
        vendorPlantDetails: {
          shipmentQtyVarianceLimit: shipmentQtyVarianceLimit || null,
        },
      }),
      validateForm: () => true,
      isFormValid: () => true,
      resetForm: () => {
        const fallbackProductTypeConfigs = buildInitialProductTypeConfigs(productTypes);
        setProductTypeConfigs(
          initialData.productTypeConfigs ||
            deriveProductTypeConfigsFromRaw(initialData, fallbackProductTypeConfigs)
        );
        const fallbackIdocFields = DEFAULT_IDOC_FIELDS.map((f) => ({ ...f, id: genId("field") }));
        setIdocFields(initialData.idocFields || deriveIdocFieldsFromRaw(initialData, fallbackIdocFields));
        setModuleMessages(initialData.moduleMessages || {});
        fetchedModuleKeysRef.current = new Set();
        setShipmentQtyVarianceLimit(initialData.shipmentQtyVarianceLimit || "");
        setModulesEditState({});
        setModulesEditPrev({});
      },
    }));

    // ---------- Product Type ERP-to-ERP Configurations ----------

    const setConnectivity = (productTypeID, value) => {
      setProductTypeConfigs((prev) => {
        const updated = prev.map((p) =>
          p.productTypeID === productTypeID
            ? { ...p, erpConnectivity: value, modules: value === "Yes" ? p.modules : [] }
            : p
        );
        notify({ productTypeConfigs: updated });
        return updated;
      });
    };

    const startEditModules = (productTypeID) => {
      const current = productTypeConfigs.find((p) => p.productTypeID === productTypeID);
      setModulesEditPrev((prev) => ({ ...prev, [productTypeID]: current?.modules || [] }));
      setModulesEditState((prev) => ({ ...prev, [productTypeID]: true }));
    };

    const setModulesDraft = (productTypeID, moduleKeys) => {
      setProductTypeConfigs((prev) =>
        prev.map((p) => (p.productTypeID === productTypeID ? { ...p, modules: moduleKeys } : p))
      );
    };

    const confirmEditModules = (productTypeID) => {
      setModulesEditState((prev) => ({ ...prev, [productTypeID]: false }));
      notify();
    };

    const cancelEditModules = (productTypeID) => {
      const prevModules = modulesEditPrev[productTypeID] || [];
      setModulesDraft(productTypeID, prevModules);
      setModulesEditState((prev) => ({ ...prev, [productTypeID]: false }));
    };

    const selectedModuleKeys = Array.from(
      new Set(
        productTypeConfigs
          .filter((p) => p.erpConnectivity === "Yes")
          .flatMap((p) => p.modules)
      )
    );
    const hasAnyErpConnectivity = productTypeConfigs.some((p) => p.erpConnectivity === "Yes");
    const yesProductTypeNames = productTypeConfigs
      .filter((p) => p.erpConnectivity === "Yes")
      .map((p) => p.productTypeName)
      .join(", ");

    const availableModuleTabs = MODULE_OPTIONS.filter((m) => selectedModuleKeys.includes(m.key));

    useEffect(() => {
      if (activeModuleTab >= availableModuleTabs.length) {
        setActiveModuleTab(0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableModuleTabs.length]);

    // Default a product type's Modules to the first module the backend actually has
    // (fetchAllModules) as soon as it's known — otherwise ERP-to-ERP Connectivity = Yes
    // with no module picked leaves Module Messages for IDocs permanently empty and the
    // fetchAllMessages call never fires. Only fills in empty selections; never
    // overwrites a module the CMO admin (or saved data) already chose.
    useEffect(() => {
      if (visibleModuleOptions.length === 0) return;
      setProductTypeConfigs((prev) => {
        let changed = false;
        const updated = prev.map((p) => {
          if (p.erpConnectivity === "Yes" && p.modules.length === 0) {
            changed = true;
            return { ...p, modules: [visibleModuleOptions[0].key] };
          }
          return p;
        });
        if (!changed) return prev;
        notify({ productTypeConfigs: updated });
        return updated;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      visibleModuleOptions.map((o) => o.key).join(","),
      productTypeConfigs.map((p) => `${p.productTypeID}:${p.erpConnectivity}:${p.modules.join("|")}`).join(","),
    ]);

    // Fetches this module's messages/Build-Versions already scoped to this CMO
    // (fetchAllVendorMessages/{collabrixModuleID}/{vendorMosymphonyID}) and stores them
    // as the live catalog for this moduleKey. The endpoint returns every Build-Version
    // available to this vendor (no Sponsor-activation flag to filter on client-side —
    // that's already applied server-side) plus this CMO's own prior selection, if any,
    // via vendorActivated/collabrixVendorMessageID (both null the first time a CMO
    // configures a message).
    const fetchMessagesForModuleKey = (moduleKey, moduleId) => {
      if (!API_BASE_URL || !vendorMosymphonyID) return;
      setSeedMessagesLoading((prev) => ({ ...prev, [moduleKey]: true }));
      axios
        .get(`${API_BASE_URL}/fetchAllVendorMessages/${moduleId}/${vendorMosymphonyID}`)
        .then((response) => {
          const list = response?.data?.messagesList || [];
          const mapped = list
            .map((msg) => {
              const versions = msg.buildsAndVersions || [];
              if (versions.length === 0) return null;
              return {
                messageId: msg.messageCode,
                versions: versions.map((v) => ({
                  // Identity key needs a separator: "1"+"10" and "11"+"0" would collide
                  // as plain concatenation. buildVersionLabel keeps the displayed text.
                  buildVersion: `${v.buildNumber}::${v.versionNumber}`,
                  buildVersionLabel: `${v.buildNumber}${v.versionNumber}`,
                  description: v.messageDescription,
                  inbound: !!v.inbound,
                  outbound: !!v.outbound,
                  collabrixMessageID: v.collabrixMessageID,
                  collabrixVendorMessageID: v.collabrixVendorMessageID,
                  excelFieldMappingFileID: v.excelFieldMappingFileID,
                  vendorActivated: v.vendorActivated,
                })),
              };
            })
            .filter(Boolean);
          setSeedMessagesByModule((prev) => ({ ...prev, [moduleKey]: mapped }));

          // Seed the CMO's own editable selection the first time this module's real
          // data arrives. A message is selected only if this CMO has an existing
          // vendor_messages record for it with activated === true; each such version
          // becomes its own row (a message can have more than one — e.g. two rows
          // sharing the same buildVersion but different collabrixVendorMessageID).
          // vendorActivated null means no vendor record yet, which defaults to
          // UNSELECTED — otherwise the first save would activate every sponsor message.
          // A message with no activated record falls back to a single unselected row at
          // the first version so the message itself still appears. Leaves an
          // already-loaded/edited selection in state alone.
          setModuleMessages((prev) => {
            if (prev[moduleKey]) return prev;
            return {
              ...prev,
              [moduleKey]: mapped.map((msg) => {
                const includedVersions = msg.versions.filter(
                  (v) => v.collabrixVendorMessageID != null && v.vendorActivated === true
                );
                const selected = includedVersions.length > 0;
                const rowSourceVersions = includedVersions.length > 0 ? includedVersions : [msg.versions[0]];
                return {
                  messageId: msg.messageId,
                  selected,
                  rows: rowSourceVersions.map((v) => ({
                    rowId: genId("row"),
                    buildVersion: v.buildVersion,
                    buildVersionLabel: v.buildVersionLabel,
                    description: v.description,
                    inbound: v.inbound,
                    outbound: v.outbound,
                    collabrixMessageID: v.collabrixMessageID,
                    collabrixVendorMessageID: v.collabrixVendorMessageID,
                    excelFieldMappingFileID: v.excelFieldMappingFileID,
                    ingestionRows: [],
                    triggerConfig: null,
                  })),
                };
              }),
            };
          });
        })
        .catch((err) => {
          console.error(`Error fetching messages for module ${moduleKey} (collabrixModuleID=${moduleId}):`, err);
        })
        .finally(() => {
          setSeedMessagesLoading((prev) => ({ ...prev, [moduleKey]: false }));
        });
    };

    useEffect(() => {
      if (apiModules === null || !vendorMosymphonyID) return;
      availableModuleTabs.forEach((tab) => {
        if (fetchedModuleKeysRef.current.has(tab.key)) return;
        const moduleId = getModuleIdForKey(tab.key);
        if (!moduleId) return;
        fetchedModuleKeysRef.current.add(tab.key);
        fetchMessagesForModuleKey(tab.key, moduleId);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiModules, vendorMosymphonyID, availableModuleTabs.map((t) => t.key).join(",")]);

    // ---------- IDoc Configurations ----------

    const addIdocField = () => {
      setIdocFields((prev) => {
        const updated = [
          ...prev,
          { id: genId("field"), field: "", isDefault: false, leadingZeros: "No", maxCharLength: "" },
        ];
        notify({ idocFields: updated });
        return updated;
      });
    };

    const removeIdocField = (id) => {
      setIdocFields((prev) => {
        const updated = prev.filter((f) => f.id !== id);
        notify({ idocFields: updated });
        return updated;
      });
    };

    const updateIdocField = (id, field, value) => {
      setIdocFields((prev) => {
        const updated = prev.map((f) => {
          if (f.id !== id) return f;
          const next = { ...f, [field]: value };
          if (field === "leadingZeros" && value === "No") next.maxCharLength = "";
          return next;
        });
        notify({ idocFields: updated });
        return updated;
      });
    };

    const usedFieldNames = idocFields.map((f) => f.field).filter(Boolean);

    // ---------- Module Messages for IDocs ----------

    const activeModuleKey = availableModuleTabs[activeModuleTab]?.key;
    const activeMessages = activeModuleKey ? moduleMessages[activeModuleKey] || [] : [];

    const updateModuleMessages = (moduleKey, updaterFn) => {
      setModuleMessages((prev) => {
        const updated = { ...prev, [moduleKey]: updaterFn(prev[moduleKey] || []) };
        notify({ moduleMessages: updated });
        return updated;
      });
    };

    const toggleMessageSelected = (moduleKey, messageId) => {
      updateModuleMessages(moduleKey, (list) =>
        list.map((m) => (m.messageId === messageId ? { ...m, selected: !m.selected } : m))
      );
    };

    const getSeedVersions = (moduleKey, messageId) =>
      (seedMessagesByModule[moduleKey] || []).find((m) => m.messageId === messageId)?.versions || [];

    const addBuildVersionRow = (moduleKey, messageId) => {
      updateModuleMessages(moduleKey, (list) =>
        list.map((m) => {
          if (m.messageId !== messageId) return m;
          const usedVersions = m.rows.filter((r) => !r.markForDelete).map((r) => r.buildVersion);
          const nextAvailable = getSeedVersions(moduleKey, messageId).find(
            (v) => !usedVersions.includes(v.buildVersion)
          );
          if (!nextAvailable) return m;
          return {
            ...m,
            rows: [
              ...m.rows,
              {
                rowId: genId("row"),
                buildVersion: nextAvailable.buildVersion,
                buildVersionLabel: nextAvailable.buildVersionLabel,
                description: nextAvailable.description,
                inbound: !!nextAvailable.inbound,
                outbound: !!nextAvailable.outbound,
                collabrixMessageID: nextAvailable.collabrixMessageID,
                collabrixVendorMessageID: nextAvailable.collabrixVendorMessageID,
                excelFieldMappingFileID: nextAvailable.excelFieldMappingFileID,
                ingestionRows: [],
                triggerConfig: null,
              },
            ],
          };
        })
      );
    };

    // Soft-deletes a row: hidden from the table immediately, but kept in state (with
    // markForDelete: true) so getPayload() still sends it to the backend for deletion.
    // getPayload() skips the deletion for a row with no collabrixVendorMessageID (no
    // vendor_messages record to delete).
    const removeBuildVersionRow = (moduleKey, messageId, rowId) => {
      updateModuleMessages(moduleKey, (list) =>
        list.map((m) => {
          if (m.messageId !== messageId) return m;
          const visibleRows = m.rows.filter((r) => !r.markForDelete);
          if (visibleRows.length <= 1) return m;
          return {
            ...m,
            rows: m.rows.map((r) => (r.rowId === rowId ? { ...r, markForDelete: true } : r)),
          };
        })
      );
    };

    // Swapping a row's Build-Version points it at a different message, so the previous
    // version's vendor_messages record (if any) is kept as a hidden markForDelete row
    // for getPayload() to delete, rather than silently dropped.
    const changeBuildVersion = (moduleKey, messageId, rowId, newBuildVersion) => {
      updateModuleMessages(moduleKey, (list) =>
        list.map((m) => {
          if (m.messageId !== messageId) return m;
          const seedVersion = getSeedVersions(moduleKey, messageId).find(
            (v) => v.buildVersion === newBuildVersion
          );
          return {
            ...m,
            rows: m.rows.flatMap((r) => {
              if (r.rowId !== rowId) return [r];
              const updated = {
                ...r,
                buildVersion: newBuildVersion,
                buildVersionLabel: seedVersion?.buildVersionLabel,
                description: seedVersion?.description || "",
                inbound: !!seedVersion?.inbound,
                outbound: !!seedVersion?.outbound,
                collabrixMessageID: seedVersion?.collabrixMessageID,
                collabrixVendorMessageID: seedVersion?.collabrixVendorMessageID,
                excelFieldMappingFileID: seedVersion?.excelFieldMappingFileID,
              };
              if (!r.collabrixVendorMessageID || r.collabrixVendorMessageID === updated.collabrixVendorMessageID) {
                return [updated];
              }
              return [updated, { ...r, rowId: genId("row"), markForDelete: true }];
            }),
          };
        })
      );
    };

    // Revokes the previous preview Blob URL (if any) before replacing/clearing it, so we
    // don't leak object URLs across repeated "Click to view" opens.
    const releaseViewFileURL = () => {
      setViewFileURL((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };

    // Downloads the real Parameters Excel for this row's Build-Version (same
    // downloadCustomFile endpoint the Sponsor Module Messages tab uses) instead of
    // showing a generic mock segment table.
    const fetchDownloadCustomFile = (excelFieldMappingFileID) => {
      if (!API_BASE_URL) return;
      setIsFileLoading(true);
      setFileLoadError(null);
      releaseViewFileURL();
      setViewFileRows([]);
      axios
        .post(`${API_BASE_URL}/downloadCustomFile`, { schrockenCustomFileID: excelFieldMappingFileID })
        .then((response) => {
          if (!response?.data?.status || !response.data.fileData) {
            setFileLoadError("No parameters file was returned for this build/version.");
            return;
          }
          const { fileData, fileName } = response.data;
          const byteArray = new Uint8Array(fileData.data);

          const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          setViewFileURL(URL.createObjectURL(blob));
          setViewFileName(fileName || "parameters.xlsx");

          const workbook = XLSX.read(byteArray, { type: "array" });
          const fieldMappingSheetName =
            workbook.SheetNames.find((name) => /field.?mapping/i.test(name)) || workbook.SheetNames[0];
          const sheet = workbook.Sheets[fieldMappingSheetName];
          setViewFileRows(XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }));
        })
        .catch((err) => {
          console.error(`Error downloading custom file (schrockenCustomFileID=${excelFieldMappingFileID}):`, err);
          setFileLoadError("Something went wrong while loading this file. Please try again.");
        })
        .finally(() => {
          setIsFileLoading(false);
        });
    };

    const openViewModal = (row, messageId) => {
      setFileLoadError(null);
      setViewSearch("");
      setViewModal({
        description: row.description,
        excelFieldMappingFileID: row.excelFieldMappingFileID,
        messageId,
        buildVersion: row.buildVersionLabel,
      });
      if (row.excelFieldMappingFileID != null) {
        fetchDownloadCustomFile(row.excelFieldMappingFileID);
      }
    };
    const closeViewModal = () => {
      setViewModal(null);
      releaseViewFileURL();
      setViewFileRows([]);
    };

    const findRow = (moduleKey, messageId, rowId) => {
      const message = (moduleMessages[moduleKey] || []).find((m) => m.messageId === messageId);
      const row = message?.rows.find((r) => r.rowId === rowId);
      return { message, row };
    };

    // Ingestion configuration is handled entirely by the real, API-wired
    // ConfigureIDocIngestionDialog — it fetches its own seed data and existing config via
    // collabrixMessageID/vendorMosymphonyID and saves directly to
    // /updateIngestionConfiguration, so no local draft state is needed here.
    const openIngestionModal = (moduleKey, messageId, rowId) => {
      setIngestionModal({ moduleKey, messageId, rowId });
    };
    const closeIngestionModal = () => {
      setIngestionModal(null);
    };
    const saveIngestionModal = (savedRows) => {
      if (!ingestionModal) return;
      const { moduleKey, messageId, rowId } = ingestionModal;
      updateModuleMessages(moduleKey, (list) =>
        list.map((m) =>
          m.messageId !== messageId
            ? m
            : { ...m, rows: m.rows.map((r) => (r.rowId === rowId ? { ...r, ingestionRows: savedRows } : r)) }
        )
      );
      closeIngestionModal();
    };

    // Trigger Point configuration is handled entirely by the real, API-wired
    // ConfigureIDocTriggerPointDialog (same component the Sponsor Module Messages tab
    // uses) — it fetches its own seed data and existing config via collabrixMessageID
    // and saves directly to /updateMessageTrigger, so no local draft state is needed here.
    const openTriggerModal = (moduleKey, messageId, rowId) => {
      setTriggerModal({ moduleKey, messageId, rowId });
    };
    const closeTriggerModal = () => {
      setTriggerModal(null);
    };
    const saveTriggerModal = (result) => {
      if (!triggerModal) return;
      const { moduleKey, messageId, rowId } = triggerModal;
      updateModuleMessages(moduleKey, (list) =>
        list.map((m) =>
          m.messageId !== messageId
            ? m
            : { ...m, rows: m.rows.map((r) => (r.rowId === rowId ? { ...r, triggerConfig: result } : r)) }
        )
      );
      closeTriggerModal();
    };

    const ingestionCtx = ingestionModal ? findRow(ingestionModal.moduleKey, ingestionModal.messageId, ingestionModal.rowId) : null;
    const triggerCtx = triggerModal ? findRow(triggerModal.moduleKey, triggerModal.messageId, triggerModal.rowId) : null;

    // viewFileRows comes from XLSX.utils.sheet_to_json(sheet, { header: 1 }) — an array
    // of row-arrays, first row is whatever header the uploaded file has.
    const viewFileHeader = viewFileRows[0] || [];
    const viewFileBodyRows = viewFileRows.slice(1).filter((row) => {
      const term = viewSearch.trim().toLowerCase();
      if (!term) return true;
      return row.some((cell) => String(cell).toLowerCase().includes(term));
    });

    return (
      <div>
        {/* ---------- Product Type ERP-to-ERP Configurations ---------- */}
        <div className={classes.sectionBlock}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" className={classes.mainHeading}>
              Product Type ERP-to-ERP Configurations
            </Typography>
            {headerActions}
          </div>
          <Table className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableCell}>Product Type</TableCell>
                <TableCell className={classes.tableCell}>ERP-to-ERP Connectivity</TableCell>
                <TableCell className={classes.tableCell}>Modules</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {productTypeConfigs.map((p) => {
                const isEditingModules = !!modulesEditState[p.productTypeID];
                const connectivityYes = p.erpConnectivity === "Yes";
                return (
                  <TableRow key={p.productTypeID}>
                    <TableCell className={classes.tableCell}>{p.productTypeName}</TableCell>
                    <TableCell className={classes.tableCell}>
                      <Select
                        value={p.erpConnectivity}
                        onChange={(e) => setConnectivity(p.productTypeID, e.target.value)}
                        disabled={readOnly}
                        classes={{ disabled: classes.selectDisabledText }}
                        displayEmpty
                      >
                        <MenuItem value="No">No</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {!connectivityYes ? (
                        <span className={classes.na}>NA</span>
                      ) : isEditingModules ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Select
                            multiple
                            displayEmpty
                            value={p.modules}
                            onChange={(e) => setModulesDraft(p.productTypeID, e.target.value)}
                            renderValue={(selected) =>
                              selected.length
                                ? selected
                                    .map((key) => MODULE_OPTIONS.find((m) => m.key === key)?.label || key)
                                    .join(", ")
                                : "Select modules"
                            }
                            style={{ minWidth: 260 }}
                          >
                            {visibleModuleOptions.map((opt) => (
                              <MenuItem key={opt.key} value={opt.key}>
                                <Checkbox checked={p.modules.includes(opt.key)} size="small" />
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <IconButton
                            size="small"
                            className={classes.iconBtn}
                            onClick={() => confirmEditModules(p.productTypeID)}
                            style={{ color: "#2e7d32" }}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            className={classes.iconBtn}
                            onClick={() => cancelEditModules(p.productTypeID)}
                            style={{ color: "#d32f2f" }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span>
                            {p.modules.length
                              ? p.modules.map((key) => MODULE_OPTIONS.find((m) => m.key === key)?.label || key).join(", ")
                              : "—"}
                          </span>
                          {!readOnly && (
                            <IconButton
                              size="small"
                              className={classes.iconBtn}
                              onClick={() => startEditModules(p.productTypeID)}
                            >
                              <EditIcon fontSize="small" className={classes.editIconOrange} />
                            </IconButton>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* ---------- IDoc Configurations ---------- */}
        {hasAnyErpConnectivity && (
          <div className={classes.sectionBlock}>
            <Typography className={classes.subHeading}>IDoc Configurations</Typography>
            <Table className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableCell}>Field</TableCell>
                  <TableCell className={classes.tableCell}>Leading Zeros</TableCell>
                  <TableCell className={classes.tableCell}>Max Character Length to be extracted</TableCell>
                  <TableCell className={classes.tableCell}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {idocFields.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell className={classes.tableCell}>
                      {f.isDefault ? (
                        f.field
                      ) : (
                        <Select
                          value={f.field}
                          onChange={(e) => updateIdocField(f.id, "field", e.target.value)}
                          disabled={readOnly}
                          classes={{ disabled: classes.selectDisabledText }}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            --Select Field--
                          </MenuItem>
                          {dataElementLabels
                            .filter((opt) => opt === f.field || !usedFieldNames.includes(opt))
                            .map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Select
                        value={f.leadingZeros}
                        onChange={(e) => updateIdocField(f.id, "leadingZeros", e.target.value)}
                        disabled={readOnly}
                        classes={{ disabled: classes.selectDisabledText }}
                        displayEmpty
                      >
                        <MenuItem value="No">No</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {f.leadingZeros === "Yes" ? (
                        <TextField
                          type="number"
                          value={f.maxCharLength}
                          onChange={(e) => updateIdocField(f.id, "maxCharLength", e.target.value)}
                          disabled={readOnly}
                          variant="outlined"
                          size="small"
                        />
                      ) : (
                        <span className={classes.na}>NA</span>
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {!f.isDefault && !readOnly && (
                        <IconButton className={classes.iconBtn} onClick={() => removeIdocField(f.id)}>
                          <DeleteOutlineIcon fontSize="small" className={classes.deleteIconRed} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!readOnly && (
              <Button className={classes.addFieldButton} onClick={addIdocField}>
                + Add New Field
              </Button>
            )}
          </div>
        )}

        {/* ---------- Module Messages for IDocs ---------- */}
        {hasAnyErpConnectivity && (
          <div className={classes.sectionBlock}>
            <Typography className={classes.subHeading}>Module Messages for IDocs</Typography>
            {availableModuleTabs.length === 0 ? (
              <div className={classes.noModulesNote}>
                Select at least one module in Product Type ERP-to-ERP Configurations to configure messages.
              </div>
            ) : (
              <>
                <div className={classes.subTabsContainer}>
                  {availableModuleTabs.map((tab, index) => (
                    <div
                      key={tab.key}
                      className={`${classes.subTab} ${index === activeModuleTab ? classes.activeSubTab : ""}`}
                      onClick={() => setActiveModuleTab(index)}
                    >
                      {tab.label}
                      {index === activeModuleTab && <div className={classes.subTabIndicator} />}
                    </div>
                  ))}
                </div>

                <Table className={classes.table}>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCell}>Selection</TableCell>
                      <TableCell className={classes.tableCell}>Message</TableCell>
                      <TableCell className={classes.tableCell}>Build-Version No.</TableCell>
                      <TableCell className={classes.tableCell}>Description</TableCell>
                      <TableCell className={classes.tableCell}>Parameters</TableCell>
                      <TableCell className={classes.tableCell}>IDoc Ingestion/Trigger Point</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {seedMessagesLoading[activeModuleKey] && activeMessages.length === 0 && (
                      <TableRow>
                        <TableCell className={classes.tableCell} colSpan={6} align="center">
                          <CircularProgress size={22} />
                        </TableCell>
                      </TableRow>
                    )}
                    {!seedMessagesLoading[activeModuleKey] && activeMessages.length === 0 && (
                      <TableRow>
                        <TableCell className={classes.tableCell} colSpan={6}>
                          No messages configured for this module.
                        </TableCell>
                      </TableRow>
                    )}
                    {activeMessages.map((m) => {
                      const visibleRows = m.rows.filter((r) => !r.markForDelete);
                      return visibleRows.map((row, rowIndex) => {
                        const isLast = rowIndex === visibleRows.length - 1;
                        const usedVersions = visibleRows.map((r) => r.buildVersion);
                        const availableVersions = getSeedVersions(activeModuleKey, m.messageId);
                        const canAddMore = availableVersions.some((v) => !usedVersions.includes(v.buildVersion));
                        return (
                          <TableRow key={row.rowId}>
                            {rowIndex === 0 && (
                              <TableCell className={classes.tableCell} rowSpan={visibleRows.length}>
                                <span style={{ position: "relative" }}>
                                  <IOSSwitch
                                    className={classes.iosSwitch}
                                    checked={m.selected}
                                    onChange={() => toggleMessageSelected(activeModuleKey, m.messageId)}
                                    disabled={readOnly}
                                  />
                                  {m.selected && <CheckIcon className={classes.checkedSwitch} />}
                                </span>
                              </TableCell>
                            )}
                            {rowIndex === 0 && (
                              <TableCell className={classes.tableCell} rowSpan={visibleRows.length}>
                                {m.messageId}
                              </TableCell>
                            )}
                            <TableCell className={classes.tableCell}>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <Select
                                  value={row.buildVersion}
                                  onChange={(e) =>
                                    changeBuildVersion(activeModuleKey, m.messageId, row.rowId, e.target.value)
                                  }
                                  disabled={readOnly}
                                  classes={{ disabled: classes.selectDisabledText }}
                                >
                                  {availableVersions
                                    .filter((v) => v.buildVersion === row.buildVersion || !usedVersions.includes(v.buildVersion))
                                    .map((v) => (
                                      <MenuItem key={v.buildVersion} value={v.buildVersion}>
                                        {v.buildVersionLabel}
                                      </MenuItem>
                                    ))}
                                </Select>
                                {!readOnly && isLast && canAddMore && (
                                  <IconButton
                                    className={classes.iconBtn}
                                    onClick={() => addBuildVersionRow(activeModuleKey, m.messageId)}
                                  >
                                    <AddCircleOutlineIcon fontSize="small" className={classes.editIconOrange} />
                                  </IconButton>
                                )}
                                {!readOnly && visibleRows.length > 1 && (
                                  <IconButton
                                    className={classes.iconBtn}
                                    onClick={() => removeBuildVersionRow(activeModuleKey, m.messageId, row.rowId)}
                                  >
                                    <DeleteOutlineIcon fontSize="small" className={classes.deleteIconRed} />
                                  </IconButton>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className={classes.tableCell}>{row.description}</TableCell>
                            <TableCell className={classes.tableCell}>
                              <span className={classes.link} onClick={() => openViewModal(row, m.messageId)}>
                                Click to view
                              </span>
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <IconButton
                                className={classes.iconBtn}
                                disabled={readOnly || (!row.inbound && !row.outbound)}
                                onClick={() => {
                                  if (row.inbound) openIngestionModal(activeModuleKey, m.messageId, row.rowId);
                                  else if (row.outbound) openTriggerModal(activeModuleKey, m.messageId, row.rowId);
                                }}
                              >
                                <EditIcon
                                  fontSize="small"
                                  className={
                                    (row.inbound || row.outbound) && !readOnly
                                      ? classes.editIconOrange
                                      : classes.editIconGray
                                  }
                                />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })}
                  </TableBody>
                </Table>

                {isShipmentModule(activeModuleKey) && (
                  <TextField
                    className={classes.varianceField}
                    label="Absolute Acceptance Limit for Shipment Qty Variance (%)"
                    required
                    type="number"
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={readOnly || !activeMessages.some((m) => m.selected)}
                    value={shipmentQtyVarianceLimit}
                    onChange={(e) => {
                      setShipmentQtyVarianceLimit(e.target.value);
                      notify({ shipmentQtyVarianceLimit: e.target.value });
                    }}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* ---------- Parameters view modal ---------- */}
        <Dialog open={!!viewModal} onClose={closeViewModal} maxWidth="lg" fullWidth>
          {viewModal && (
            <div className={classes.modalRoot}>
              <div className={classes.paramsTopBar}>
                {viewFileRows.length > 0 && (
                  <InputBase
                    placeholder="Search..."
                    value={viewSearch}
                    onChange={(e) => setViewSearch(e.target.value)}
                    className={classes.searchBox}
                  />
                )}
                {viewFileURL && (
                  <Button
                    className={classes.downloadBtn}
                    component="a"
                    href={viewFileURL}
                    download={viewFileName || "parameters.xlsx"}
                  >
                    <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
                    Download
                  </Button>
                )}
                <div className={classes.paramsCloseIcon} onClick={closeViewModal}>
                  <CloseIcon fontSize="small" />
                </div>
              </div>
              <div style={{ padding: "16px 24px 0" }}>
                {isFileLoading && (
                  <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                    <CircularProgress size={28} />
                  </div>
                )}
                {!isFileLoading && fileLoadError && <div className={classes.errorText}>{fileLoadError}</div>}
                {!isFileLoading && !fileLoadError && !viewModal.excelFieldMappingFileID && (
                  <div className={classes.na}>No parameters file is available for this build/version.</div>
                )}
              </div>
              {!isFileLoading && !fileLoadError && viewFileRows.length > 0 && (
                <div className={classes.paramsTableWrapper}>
                  <Table className={classes.paramsTable}>
                    <TableHead>
                      <TableRow>
                        {viewFileHeader.map((col, index) => (
                          <TableCell key={index} className={classes.paramsHeaderCell}>
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewFileBodyRows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {viewFileHeader.map((_, colIndex) => (
                            <TableCell key={colIndex} className={classes.paramsBodyCell}>
                              {row[colIndex]}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </Dialog>

        {/* ---------- Configure IDoc Ingestion popup ---------- */}
        <ConfigureIDocIngestionDialog
          open={!!(ingestionModal && ingestionCtx?.row)}
          onClose={closeIngestionModal}
          onSave={saveIngestionModal}
          messageInfo={
            ingestionCtx?.row
              ? {
                  name: ingestionCtx.message.messageId,
                  buildNo: ingestionCtx.row.buildVersionLabel,
                  description: ingestionCtx.row.description,
                }
              : null
          }
          collabrixMessageID={ingestionCtx?.row?.collabrixMessageID}
          collabrixVendorMessageID={ingestionCtx?.row?.collabrixVendorMessageID}
          vendorMosymphonyID={vendorMosymphonyID}
          brandOwnerMosymphonyID={brandOwnerMosymphonyID}
          cmoName={cmoName || "—"}
          productType={yesProductTypeNames || "—"}
          initialRows={ingestionCtx?.row?.ingestionRows || []}
        />

        {/* ---------- Configure IDoc Trigger Point popup ---------- */}
        <ConfigureIDocTriggerPointDialog
          open={!!(triggerModal && triggerCtx?.row)}
          onClose={closeTriggerModal}
          onSave={saveTriggerModal}
          messageInfo={
            triggerCtx?.row
              ? {
                  name: triggerCtx.message.messageId,
                  buildNo: triggerCtx.row.buildVersionLabel,
                  versionNo: "",
                  description: triggerCtx.row.description,
                }
              : null
          }
          collabrixMessageID={triggerCtx?.row?.collabrixMessageID}
          cmoName={cmoName || "—"}
          productType={yesProductTypeNames || "—"}
        />
      </div>
    );
  }
);

export default ErpToErpConfigurationSection;
